import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../config/axiosInterceptor';
import { toast } from 'react-toastify';

const EditPlan = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const planId = queryParams.get('id');

    const [plan, setPlan] = useState(null);
    const [themes, setThemes] = useState([]);
    const [frames, setFrames] = useState([]);
    const [vips, setVips] = useState([]);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        if (!planId) return;

        axiosInstance.get(`/admin/plan?id=${planId}`)
            .then(res => {
                if (res.data.success) {
                    setPlan(res.data.details);
                } else {
                    toast.error(res.data.message);
                }
            })
            .catch(err => toast.error(err.message));

        axiosInstance.get(`/admin/plan/getPlanAssets?type=1`).then(res => {
            if (res.data.success) setThemes(res.data.data);
        });

        axiosInstance.get(`/admin/plan/getPlanAssets?type=0`).then(res => {
            if (res.data.success) setFrames(res.data.data);
        });

        axiosInstance.get(`/admin/plan/getPlanAssets?type=2`).then(res => {
            if (res.data.success) setVips(res.data.data || []);
        });
    }, [planId]);

    useEffect(() => {
        if (plan) {
            const { rs = 0, SGST = 0, CGST = 0 } = plan;
            const t = parseFloat(rs) + parseFloat(SGST) + parseFloat(CGST);
            setTotal(t);
        }
    }, [plan?.rs, plan?.SGST, plan?.CGST]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPlan(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleOfferChange = (field, value) => {
        setPlan(prev => ({
            ...prev,
            offer: {
                from: prev.offer?.from || 0,
                to: prev.offer?.to || 0,
                type: prev.offer?.type || 'coins',
                active: prev.offer?.active || false,
                [field]: value
            }
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!plan.title || !plan.coins || !plan.rs) {
            toast.error("Title, Coins and INR fields are required.");
            return;
        }

        const payload = {
            id: planId,
            title: plan.title,
            coins: plan.coins,
            rs: plan.rs,
            SGST: plan.SGST,
            CGST: plan.CGST,
            total: parseFloat(plan.rs) + parseFloat(plan.SGST || 0) + parseFloat(plan.CGST || 0),
            theme: plan.theme,
            frame: plan.frame,
            vips: plan.vips || [],
            offer: {
                from: plan.offer?.from || 0,
                to: plan.offer?.to || 0,
                type: plan.offer?.type || 'coins',
                active: plan.offer?.active || false
            }
        };

        try {
            const res = await axiosInstance.put(`${process.env.REACT_APP_BASEURL5}/admin/plan/updatePlan`, { data: payload });
            if (res.data.success) {
                toast.success(res.data.message);
                setTimeout(() => navigate('/admin/managePlan'), 1000);
            } else {
                toast.error(res.data.message);
            }
        } catch (err) {
            toast.error(err.message);
        }
    };

    const ITEM_TYPE_LABELS = {
        frames: "Frame",
        entryEffects: "Entry Effect",
        bubbles: "Bubble",
        profileBadge: "Profile Badge",
        userBadge: "User Badge",
    };

    const toggleVip = (vipId) => {
        setPlan(prev => {
            const current = (prev.vips || []).map(String);
            return {
                ...prev,
                vips: current.includes(String(vipId))
                    ? current.filter(id => id !== String(vipId))
                    : [...current, String(vipId)]
            };
        });
    };

    const renderVipPicker = () => {
        const selected = (plan.vips || []).map(String);
        return (
            <div className="form-group">
                <label>Select VIPs (optional)</label>
                <p className="text-muted" style={{ fontSize: '13px', marginBottom: '8px' }}>
                    Buying this plan grants each selected VIP and every item inside it.
                </p>
                {vips.length === 0 && (
                    <p className="text-muted"><em>No VIPs with type PLAN available.</em></p>
                )}
                {vips.map(vip => (
                    <div
                        key={vip._id}
                        style={{
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '10px',
                            marginBottom: '8px',
                            background: selected.includes(String(vip._id)) ? '#f3f9ff' : '#fff'
                        }}
                    >
                        <label style={{ fontWeight: 'normal', marginBottom: 0, cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={selected.includes(String(vip._id))}
                                onChange={() => toggleVip(vip._id)}
                                style={{ marginRight: '8px' }}
                            />
                            <strong>{vip.title || "VIP"}</strong>
                            {vip.valid ? ` - ${vip.valid} days` : ''}
                        </label>
                        <div style={{ marginLeft: '24px', marginTop: '6px' }}>
                            {vip.items && vip.items.length > 0 ? (
                                <>
                                    <small className="text-muted">Includes:</small>
                                    <ul style={{ marginBottom: 0, paddingLeft: '18px' }}>
                                        {vip.items.map(item => (
                                            <li key={item._id} style={{ fontSize: '13px' }}>
                                                {ITEM_TYPE_LABELS[item.itemType] || item.itemType}: {item.title || "-"}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            ) : (
                                <small className="text-muted"><em>No items linked to this VIP yet.</em></small>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    if (!plan) return <p>Loading...</p>;

    return (
        <>
            <section className="content-header">
                <h1>Edit Plan</h1>
                <ol className="breadcrumb">
                    <li><Link to="/admin"><i className="fa fa-dashboard" /> Home</Link></li>
                    <li><Link to="/admin/managePlan">Manage Plan</Link></li>
                    <li className="active">Edit Plan</li>
                </ol>
            </section>

            <section className="content">
                <div className="row">
                    <div className="col-md-12">
                        <form onSubmit={handleSubmit}>
                            <div className="box box-warning">
                                <div className="box-header with-border">
                                    <h3 className="box-title">Edit</h3>
                                </div>
                                <div className="box-body">
                                    <div className="form-group">
                                        <label>Title*</label>
                                        <input type="text" className="form-control" name="title" value={plan.title} onChange={handleChange} />
                                    </div>
                                    <div className="form-group">
                                        <label>Coin*</label>
                                        <input type="number" className="form-control" name="coins" value={plan.coins} onChange={handleChange} />
                                    </div>
                                    <div className="form-group">
                                        <label>Rupees (INR)*</label>
                                        <input type="number" className="form-control" name="rs" value={plan.rs} onChange={handleChange} />
                                    </div>
                                    <div className="form-group">
                                        <label>SGST</label>
                                        <input type="number" className="form-control" name="SGST" value={plan.SGST} onChange={handleChange} />
                                    </div>
                                    <div className="form-group">
                                        <label>CGST</label>
                                        <input type="number" className="form-control" name="CGST" value={plan.CGST} onChange={handleChange} />
                                    </div>
                                    <div className="form-group">
                                        <label>Total Amount</label>
                                        <input type="number" className="form-control" value={total} readOnly />
                                    </div>
                                    <div className="form-group">
                                        <label>Select Theme</label>
                                        <select className="form-control" name="theme" value={plan.theme} onChange={handleChange}>
                                            <option value="">-- Select Theme --</option>
                                            {themes.map(theme => (
                                                <option key={theme._id} value={theme._id}>
                                                    {theme.name ?? "THEME"} ({theme.valid} hours)
                                                </option>
                                            ))}
                                        </select>
                                        {themes.map(theme => (
                                            plan.theme === theme._id &&
                                            <img key={theme._id} src={`${process.env.REACT_APP_BASEURL}${theme.image}`} alt={theme.name} style={{ height: '100px', marginTop: '10px' }} />
                                        ))}
                                    </div>
                                    <div className="form-group">
                                        <label>Select Frame</label>
                                        <select className="form-control" name="frame" value={plan.frame} onChange={handleChange}>
                                            <option value="">-- Select Frame --</option>
                                            {frames.map(frame => (
                                                <option key={frame._id} value={frame._id}>
                                                    {frame.title ?? "FRAME"} ({frame.valid} hours)
                                                </option>
                                            ))}
                                        </select>
                                        {frames.map(frame => (
                                            plan.frame === frame._id &&
                                            <img key={frame._id} src={`${process.env.REACT_APP_BASEURL}${frame.thumbnail}`} alt={frame.name} style={{ height: '100px', marginTop: '10px' }} />
                                        ))}
                                    </div>

                                    {renderVipPicker()}

                                    <div className="form-group">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={plan.offer?.active || false}
                                                onChange={(e) => handleOfferChange('active', e.target.checked)}
                                                style={{ marginRight: '8px' }}
                                            />
                                            Enable Offer
                                        </label>
                                    </div>
                                    {plan.offer?.active && (
                                        <>
                                            <div className="form-group">
                                                <label>Offer Type</label>
                                                <select className="form-control" value={plan.offer?.type || 'coins'} onChange={(e) => handleOfferChange('type', e.target.value)}>
                                                    <option value="coins">Coins</option>
                                                    <option value="percentage">Percentage</option>
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label>Offer From</label>
                                                <input type="number" className="form-control" value={plan.offer?.from || 0} onChange={(e) => handleOfferChange('from', e.target.value)} />
                                            </div>
                                            <div className="form-group">
                                                <label>Offer To</label>
                                                <input type="number" className="form-control" value={plan.offer?.to || 0} onChange={(e) => handleOfferChange('to', e.target.value)} />
                                            </div>
                                            <p className="text-muted">
                                                {plan.offer?.type === 'percentage'
                                                    ? `A random ${plan.offer?.from || 0}% to ${plan.offer?.to || 0}% bonus of plan coins will be added on purchase.`
                                                    : `A random amount between ${plan.offer?.from || 0} and ${plan.offer?.to || 0} bonus coins will be added on purchase.`}
                                            </p>
                                        </>
                                    )}
                                    <div className="form-group">
                                        <button type="reset" className="btn btn-danger">Cancel</button>
                                        <input type="submit" className="btn btn-success pull-right" value="Update" />
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};

export default EditPlan;
